import { useState } from 'react';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  useGetCubeDefinitionDetails,
  useGetDropdownsCubePanel,
} from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { CubeDefinitionItem } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import { CubeRestructureType, RequestCubeRestructureCheck } from 'types/api/SystemTenant/AROCube/CubeRestructureTypes';
import { CubeCurrentStructure } from './CubeCurrentStructure';
import { CubePendingDependencySync } from './CubePendingDependencySync';
import { CubeRestructureImpact } from './CubeRestructureImpact';
import { useCubeRestructureFlow } from './useCubeRestructureFlow';

const restructureOptions: {
  value: CubeRestructureType;
  label: string;
  targetField: 'memberId' | 'timePeriodSetId' | 'cubeLayerSetId';
  targetLabel: string;
  description: string;
  retention: string;
}[] = [
  {
    value: 'CHANGE_MEMBER',
    label: 'Change Main Dimension (KTree)',
    targetField: 'memberId',
    targetLabel: 'Main Dimension (KTree)',
    description: 'Select the KTree definition to use as the new main dimension.',
    retention: 'Match base members by their underlying member identity, remap row IDs, and rebuild parent totals.',
  },
  {
    value: 'CHANGE_TIME_PERIOD_SET',
    label: 'Change Time Period Set',
    targetField: 'timePeriodSetId',
    targetLabel: 'Time Period Set',
    description: 'Select a new time period set with the same interval as the source cube.',
    retention: 'Retain values in overlapping periods with the same interval.',
  },
  {
    value: 'CHANGE_LAYER_SET',
    label: 'Change Layer Set',
    targetField: 'cubeLayerSetId',
    targetLabel: 'Layer Set',
    description: 'Select the new set of layers to use for this cube.',
    retention: 'Retain values for shared layer IDs; newly added layers begin without migrated values.',
  },
];

interface ModalRestructureCubeDefinitionProps {
  cubes: CubeDefinitionItem[];
  systemTenantId: string;
  onClose: () => void;
}

export function ModalRestructureCubeDefinition({
  cubes,
  systemTenantId,
  onClose,
}: ModalRestructureCubeDefinitionProps) {
  const [sourceCubeId, setSourceCubeId] = useState('');
  const [restructureType, setRestructureType] = useState<CubeRestructureType | ''>('');
  const [targetId, setTargetId] = useState('');
  const sourceDetails = useGetCubeDefinitionDetails(sourceCubeId || undefined);
  const source =
    sourceDetails.isSuccess && sourceDetails.data?.data.id === sourceCubeId ? sourceDetails.data.data : undefined;
  const subjectId = source?.subject?.id?.toString();
  const dropdowns = useGetDropdownsCubePanel(subjectId, sourceCubeId || undefined);
  const sourceLoading = Boolean(sourceCubeId) && sourceDetails.isFetching;
  const targetLoading = sourceLoading || dropdowns.isFetching;
  const targetDisabled = !subjectId || targetLoading || sourceDetails.isError || dropdowns.isError;
  const selectedType = restructureOptions.find((option) => option.value === restructureType);
  const sourceOptions = cubes.map((cube) => ({
    value: cube.id,
    label: `${cube.cubeCode} · ${cube.cubeName} · Subject: ${cube.subjectDisplayName}`,
  }));

  const currentTarget =
    restructureType === 'CHANGE_MEMBER'
      ? source?.member
      : restructureType === 'CHANGE_TIME_PERIOD_SET'
      ? source?.timePeriodSet
      : source?.layerSet;
  // The dropdown API uses currentCubeId to return every set with the source cube's interval.
  // Cube details only provide the current set's ID and label, so they cannot be used to filter by type.
  const availableTargets: AutoCompleteItem[] =
    restructureType === 'CHANGE_MEMBER'
      ? dropdowns.data?.data.member || []
      : restructureType === 'CHANGE_TIME_PERIOD_SET'
      ? dropdowns.data?.data.timePeriod || []
      : dropdowns.data?.data.layerSet || [];
  const targetOptions = availableTargets.filter(
    (option) => option.value.toString() !== currentTarget?.value.toString()
  );

  let payload: RequestCubeRestructureCheck | null = null;
  if (
    !targetDisabled &&
    sourceOptions.some((option) => option.value === sourceCubeId) &&
    targetOptions.some((option) => option.value.toString() === targetId)
  ) {
    if (restructureType === 'CHANGE_MEMBER') payload = { cubeId: sourceCubeId, restructureType, memberId: targetId };
    if (restructureType === 'CHANGE_TIME_PERIOD_SET')
      payload = { cubeId: sourceCubeId, restructureType, timePeriodSetId: targetId };
    if (restructureType === 'CHANGE_LAYER_SET')
      payload = { cubeId: sourceCubeId, restructureType, cubeLayerSetId: targetId };
  }
  const flow = useCubeRestructureFlow(systemTenantId, payload);
  const finished = flow.result?.status === 'COMPLETED' || flow.result?.status === 'CANCELLED';
  const impact = flow.result?.dataImpact || flow.preview;
  const handleClose = () => {
    if (!flow.busy) onClose();
  };

  const renderDropdown = ({
    name,
    label,
    description,
    options,
    value,
    onChange,
    disabled = false,
    loading = false,
    current,
  }: {
    name: string;
    label: string;
    description: string;
    options: AutoCompleteItem[];
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    loading?: boolean;
    current?: string;
  }) => (
    <Stack spacing={0.75}>
      <Typography component='label' htmlFor={`restructure-cube-${name}`} variant='input-label' fontWeight='bold'>
        {label}
      </Typography>
      <Typography id={`restructure-cube-${name}-description`} variant='input-label-gray' component='p'>
        {description}
        {current && ` Current: ${current}.`}
      </Typography>
      <Autocomplete
        id={`restructure-cube-${name}`}
        size='small'
        fullWidth
        options={[...options].sort((a, b) =>
          a.label.localeCompare(b.label, 'en', { numeric: true, sensitivity: 'base' })
        )}
        value={options.find((option) => option.value.toString() === value) || null}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.value.toString() === selected.value.toString()}
        onChange={(_event, option) => onChange(option?.value.toString() || '')}
        disabled={disabled || Boolean(flow.request) || flow.busy}
        loading={loading}
        noOptionsText={name === selectedType?.targetField ? 'No alternative options available' : 'No options available'}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            placeholder='Select an option'
            required
            inputProps={{ ...params.inputProps, 'aria-describedby': `restructure-cube-${name}-description` }}
          />
        )}
      />
    </Stack>
  );

  return (
    <Dialog open onClose={handleClose} maxWidth='sm' fullWidth aria-labelledby='restructure-cube-title'>
      <Stack sx={{ minHeight: 0 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ px: 2, py: 1.5 }}>
          <Typography
            id='restructure-cube-title'
            variant='body1'
            component='h2'
            fontWeight='bold'
            sx={{ color: '#3B4797' }}
          >
            Restructure a Cube
          </Typography>
          <IconButton aria-label='Close' onClick={handleClose} disabled={flow.busy}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>
        <DialogContent
          sx={{ bgcolor: '#FBFBFB', px: 3, py: 3, borderTop: '1px solid #E3EBF6', borderBottom: '1px solid #E3EBF6' }}
        >
          <Stack spacing={2.5}>
            {flow.error && <Alert severity='error'>{flow.error}</Alert>}
            {flow.request ? (
              <Stack spacing={1.5} aria-live='polite'>
                <Alert
                  severity={
                    flow.result?.status === 'COMPLETED'
                      ? 'success'
                      : flow.result?.status === 'INCOMPLETE'
                      ? 'warning'
                      : 'info'
                  }
                >
                  <Typography variant='body2' fontWeight='bold'>
                    {flow.result?.status === 'COMPLETED'
                      ? 'Cube restructured successfully'
                      : flow.result?.status === 'CANCELLED'
                      ? 'Restructure cancelled'
                      : flow.result?.status === 'INCOMPLETE'
                      ? 'Restructuring needs attention'
                      : flow.result?.status === 'IN_PROGRESS'
                      ? 'Restructuring in progress'
                      : 'Checking restructure operation'}
                  </Typography>
                  {flow.result && <Typography variant='body2'>{flow.result.message}</Typography>}
                </Alert>
                {(flow.busy || flow.result?.status === 'IN_PROGRESS') && (
                  <Stack direction='row' spacing={1} alignItems='center' role='status'>
                    <CircularProgress size={16} aria-label='Restructure progress' />
                    <Typography variant='body2'>
                      {flow.result?.phase === 'INDEXING'
                        ? 'Updating the cube index...'
                        : 'Processing restructure operation...'}
                    </Typography>
                  </Stack>
                )}
                <Typography variant='body2' sx={{ overflowWrap: 'anywhere', userSelect: 'text' }}>
                  Operation: {flow.request.operationId}
                </Typography>
                {!finished && (
                  <Typography variant='body2' color='text.secondary'>
                    Reopen Restructure a Cube to resume this operation if you close the dialog.
                  </Typography>
                )}
                {impact && <CubeRestructureImpact data={impact} preview={false} />}
              </Stack>
            ) : (
              <>
                {renderDropdown({
                  name: 'sourceCubeId',
                  label: 'Source Cube',
                  description: 'Select the existing cube whose structure you want to change.',
                  options: sourceOptions,
                  value: sourceCubeId,
                  onChange: (value) => {
                    setSourceCubeId(value);
                    setTargetId('');
                  },
                })}
                {sourceLoading && (
                  <Stack direction='row' spacing={1} alignItems='center' role='status'>
                    <CircularProgress size={16} aria-label='Loading source cube' />
                    <Typography variant='body2'>Loading source cube...</Typography>
                  </Stack>
                )}
                {source && !sourceLoading && (
                  <CubeCurrentStructure
                    cube={source}
                    subjectName={cubes.find((cube) => cube.id === sourceCubeId)?.subjectDisplayName}
                    titleId='restructure-current-structure-title'
                  />
                )}
                {renderDropdown({
                  name: 'restructureType',
                  label: 'Restructure Type',
                  description: 'Choose which part of the cube structure to change.',
                  options: restructureOptions,
                  value: restructureType,
                  onChange: (value) => {
                    setRestructureType(value as CubeRestructureType | '');
                    setTargetId('');
                  },
                })}
                {(sourceDetails.isError || dropdowns.isError) && (
                  <Alert
                    severity='error'
                    action={
                      <Button
                        color='inherit'
                        size='small'
                        onClick={() => {
                          if (sourceDetails.isError) sourceDetails.refetch();
                          if (dropdowns.isError) dropdowns.refetch();
                        }}
                      >
                        Retry
                      </Button>
                    }
                  >
                    {sourceDetails.isError
                      ? 'Unable to load the source cube. Please try again.'
                      : 'Unable to load restructure options. Please try again.'}
                  </Alert>
                )}
                {source && !sourceLoading && !subjectId && (
                  <Alert severity='error'>The selected source cube has no subject. Choose another source cube.</Alert>
                )}
                {selectedType && (
                  <>
                    {renderDropdown({
                      name: selectedType.targetField,
                      label: selectedType.targetLabel,
                      description: selectedType.description,
                      options: targetOptions,
                      value: targetId,
                      onChange: setTargetId,
                      disabled: targetDisabled,
                      loading: targetLoading,
                      current: currentTarget?.label,
                    })}
                    {!targetId && (
                      <Typography variant='body2' color='text.secondary'>
                        Data remapping is checked automatically after you select a target.
                      </Typography>
                    )}
                    <Stack spacing={0.75} sx={{ borderTop: '1px solid #E3EBF6', pt: 2 }} aria-live='polite'>
                      <Typography variant='input-label' fontWeight='bold'>
                        Data retention
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {selectedType.retention}
                      </Typography>
                    </Stack>
                  </>
                )}
                {flow.checking && (
                  <Stack direction='row' spacing={1} alignItems='center' role='status'>
                    <CircularProgress size={16} aria-label='Checking restructure data' />
                    <Typography variant='body2'>Checking the data mapping...</Typography>
                  </Stack>
                )}
                {flow.preview && <CubeRestructureImpact data={flow.preview} />}
                {sourceCubeId && (
                  <CubePendingDependencySync
                    key={sourceCubeId}
                    cubeId={sourceCubeId}
                    failed={Boolean(flow.error || flow.preview?.blockers.length)}
                    onResolved={() => void flow.checkData()}
                  />
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <Stack direction='row' justifyContent='flex-end' flexWrap='wrap' gap={1.5} sx={{ px: 2, py: 2 }}>
          <Button variant='main-table-panel-border' onClick={handleClose} disabled={flow.busy}>
            {flow.request ? 'Close' : 'Cancel'}
          </Button>
          {flow.request ? (
            !finished && (
              <>
                {flow.result?.phase === 'PREPARED' && (
                  <Button variant='main-table-panel-border' disabled={flow.busy} onClick={() => void flow.cancel()}>
                    Cancel Restructure
                  </Button>
                )}
                <Button variant='secondary-table-panel' disabled={flow.busy} onClick={() => void flow.refreshStatus()}>
                  Check Status
                </Button>
                {(flow.result?.status !== 'IN_PROGRESS' || flow.error) && (
                  <Button variant='main-table-panel' disabled={flow.busy} onClick={() => void flow.retry()}>
                    Retry Restructure
                  </Button>
                )}
              </>
            )
          ) : (
            <>
              {payload && (flow.preview || flow.error) && (
                <Button variant='secondary-table-panel' disabled={flow.checking} onClick={() => void flow.checkData()}>
                  {flow.preview ? 'Recheck Data Mapping' : 'Retry Data Check'}
                </Button>
              )}
              <Button variant='main-table-panel' disabled={!flow.canExecute} onClick={flow.start}>
                Restructure Cube
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}
